import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { VideoPlayOverlay } from "../../components/video-play-overlay";
import { videoSecondsPlayed } from "../../components/video-seconds-played";
import { embedBookingUrl, isBookingConfirmedMessage } from "./booking-embed";
import explainerPosterUrl from "./credit-incentive-assets/explainer-poster.jpg?url";
import * as styles from "./credit-incentive-modal.module.css";
import { getCreditIncentiveSubtitleUrl } from "./subtitles";

/** Long form of https://calendar.app.google/ZZHT4WWfAv6M13K79; the short link cannot be framed. */
const CREDIT_INCENTIVE_BOOKING_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ0NKBpYQ8_Ma04fObnjGpUeXxSW2gkp4Nvw-7Lyw1gHmK9oXIgV6RePvYYQj7tN9AboofBMhMCH";

const CREDIT_INCENTIVE_LEARN_MORE_URL =
  "https://redo.com/products/credit-incentive";

const CREDIT_INCENTIVE_VIDEO_URL =
  "https://videos.redo.com/CreditIncentiveExplainer.mp4";

type View = "pitch" | "calendar" | "booked";

/**
 * Draws the active cue in one fixed spot. Chrome relays out native captions
 * when its controls appear but not when they hide, so a cue jumps up at once
 * and drifts back down only when the next one starts. The native track stays
 * on for fullscreen, where nothing outside the video is visible.
 */
function useActiveCue(
  videoRef: RefObject<HTMLVideoElement | null>,
): string | null {
  const [cue, setCue] = useState<string | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    const track = video?.textTracks[0];
    if (!video || !track) {
      return;
    }
    const syncMode = () => {
      track.mode = document.fullscreenElement === video ? "showing" : "hidden";
    };
    const onCueChange = () => {
      const active = track.activeCues?.[0];
      setCue(
        active instanceof VTTCue
          ? (active.getCueAsHTML().textContent ?? "").trim()
          : null,
      );
    };
    syncMode();
    track.addEventListener("cuechange", onCueChange);
    document.addEventListener("fullscreenchange", syncMode);
    return () => {
      track.removeEventListener("cuechange", onCueChange);
      document.removeEventListener("fullscreenchange", syncMode);
    };
  }, [videoRef]);
  return cue;
}

/** Chrome hides its control bar this long after the pointer last moved. */
const CONTROLS_HIDE_MS = 3000;

/**
 * Whether Chrome's control bar is on screen, as far as it can be known from
 * outside: shown while paused, on pointer movement, and for a moment after
 * play; hidden when the pointer leaves or goes still. The caption follows it.
 */
function useControlsShown(videoRef: RefObject<HTMLVideoElement | null>) {
  const [controlsShown, setControlsShown] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const hide = () => {
    window.clearTimeout(hideTimer.current);
    if (!videoRef.current?.paused) {
      setControlsShown(false);
    }
  };
  const show = () => {
    setControlsShown(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(hide, CONTROLS_HIDE_MS);
  };
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);
  return {
    controlsShown,
    onPointerMove: show,
    onPointerLeave: hide,
    onPlay: show,
    onPause: () => {
      window.clearTimeout(hideTimer.current);
      setControlsShown(true);
    },
  };
}

/** What the merchant gets, one line each, beside a green check. */
const BENEFITS = [
  "Increase repeat purchase rate by 22% within 60 days",
  "Carts using the credit run 20% larger",
  "Not offering Checkout+ yet? We'll set it up on the call",
];

/** The explainer, in the band the design reserves for a hero image. */
const Hero = memo(function Hero({
  videoRef,
}: {
  videoRef: RefObject<HTMLVideoElement>;
}) {
  const [playing, setPlaying] = useState(false);
  const subtitleUrl = useMemo(() => getCreditIncentiveSubtitleUrl(), []);
  const cue = useActiveCue(videoRef);
  const controls = useControlsShown(videoRef);
  return (
    <div
      className={styles.videoFrame}
      onPointerLeave={controls.onPointerLeave}
      onPointerMove={controls.onPointerMove}
    >
      {/* `metadata` keeps the 29 MB file off the wire until the play click. */}
      <video
        className={styles.video}
        controls={playing}
        onPause={controls.onPause}
        onPlay={controls.onPlay}
        playsInline
        poster={explainerPosterUrl}
        preload="metadata"
        ref={videoRef}
        src={CREDIT_INCENTIVE_VIDEO_URL}
        title="How Credit Incentive works"
      >
        <track
          default
          kind="subtitles"
          label="English"
          src={subtitleUrl}
          srcLang="en"
        />
      </video>
      {playing && cue ? (
        <Flex
          aria-hidden
          className={classNames(
            styles.captions,
            controls.controlsShown && styles.captionsRaised,
          )}
          justify="center"
          pointerEvents="none"
          position="absolute"
        >
          <Flex borderRadius="sm" className={styles.caption} px="lg" py="xs">
            <Text fontSize="md" fontWeight="medium" textAlign="center">
              {cue}
            </Text>
          </Flex>
        </Flex>
      ) : null}
      {playing ? null : (
        <VideoPlayOverlay
          label="Play How Credit Incentive works"
          onPlay={() => {
            setPlaying(true);
            void videoRef.current?.play();
          }}
        />
      )}
    </div>
  );
});

/** The calendar in place of the pitch; a confirmed booking is reported once. */
const BookingCalendar = memo(function BookingCalendar({
  onBooked,
}: {
  onBooked: () => void;
}) {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (isBookingConfirmedMessage(event, CREDIT_INCENTIVE_BOOKING_URL)) {
        onBooked();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onBooked]);

  return (
    <iframe
      className={styles.calendarFrame}
      src={embedBookingUrl(CREDIT_INCENTIVE_BOOKING_URL, window.location.host)}
      title="Book a 15-minute setup"
    />
  );
});

export const CreditIncentiveModal = memo(function CreditIncentiveModal({
  onDismiss,
  onBookingStarted,
  onBooked,
  onFinished,
}: {
  /** Closed from the pitch; the offer may return later. */
  onDismiss: (videoSecondsWatched: number) => void;
  /** The calendar was opened; from here the offer is taken as accepted. */
  onBookingStarted: (videoSecondsWatched: number) => void;
  /** A calendar that reports bookings confirmed a slot; the modal says so. */
  onBooked: () => void;
  /** Closed after the calendar was opened; nothing left to offer. */
  onFinished: () => void;
}) {
  const [view, setView] = useState<View>("pitch");
  const videoRef = useRef<HTMLVideoElement>(null);

  const dismiss = () => {
    onDismiss(videoSecondsPlayed(videoRef.current?.played));
  };
  const openCalendar = () => {
    onBookingStarted(videoSecondsPlayed(videoRef.current?.played));
    setView("calendar");
  };
  const openLearnMore = () => {
    window.open(
      CREDIT_INCENTIVE_LEARN_MORE_URL,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const confirmBooking = () => {
    onBooked();
    setView("booked");
  };

  if (view === "calendar") {
    return (
      <RedoBaseModal
        darkMode
        isOpen
        onModalCloseRequested={onFinished}
        otherClasses={[styles.calendarModal]}
      >
        <RedoModalHeader
          cancelClicked={onFinished}
          subtitle="Pick a 15-minute slot. We'll turn on your $10 credit together, live on the call."
          title="Pick a time"
        />
        <div className={styles.calendarBody}>
          <BookingCalendar onBooked={confirmBooking} />
        </div>
        <Flex justify="center" pb="2xl">
          <RedoButton
            hierarchy="link"
            onClick={() =>
              window.open(
                CREDIT_INCENTIVE_BOOKING_URL,
                "_blank",
                "noopener,noreferrer",
              )
            }
            size="sm"
            text="Open the calendar in a new tab"
          />
        </Flex>
      </RedoBaseModal>
    );
  }

  if (view === "booked") {
    return (
      <RedoBaseModal darkMode isOpen onModalCloseRequested={onFinished}>
        <RedoModalHeader
          cancelClicked={onFinished}
          hideCloseButton
          subtitle="We'll turn on your $10 credit together on the call. Watch for the calendar invite."
          title="You're booked"
        />
        <RedoModalFooter
          primaryButton={{
            onClick: onFinished,
            size: "sm",
            text: "Done",
            theme: "action",
          }}
        />
      </RedoBaseModal>
    );
  }

  return (
    <RedoBaseModal
      darkMode
      isOpen
      onModalCloseRequested={dismiss}
      otherClasses={[styles.modal]}
    >
      <Flex pl="3xl" pr="3xl" pt="3xl">
        <Hero videoRef={videoRef} />
      </Flex>
      <Flex className={styles.body} dir="column" gap="xl" p="3xl">
        <Flex>
          <Flex align="center" className={styles.pill} px="md" py="xxs">
            <Text className={styles.pillText} fontSize="xs" fontWeight="medium">
              Free
            </Text>
          </Flex>
        </Flex>
        <Flex dir="column" gap="md">
          <Text
            as="h2"
            className={styles.title}
            fontFamily="serif"
            fontSize="3xl"
            fontWeight="regular"
          >
            Give shoppers $10 back and grow sales 5.5%
          </Text>
          <Text className={styles.subtitle} fontSize="md">
            Shoppers who opt in to Checkout+ earn a $10 credit toward their next
            order. More say yes now, many come back to spend it.
          </Text>
        </Flex>
        <Flex dir="column" gap="md">
          {BENEFITS.map((benefit) => (
            <Flex align="center" gap="md" key={benefit}>
              <Flex
                align="center"
                className={styles.checkMark}
                justify="center"
              >
                <CheckIcon height={12} width={12} />
              </Flex>
              <Text className={styles.benefit} fontSize="md">
                {benefit}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <RedoModalFooter
        additionalContent={
          <RedoButton
            hierarchy="secondary"
            onClick={dismiss}
            size="sm"
            text="Not now"
          />
        }
        primaryButton={{
          onClick: openCalendar,
          size: "sm",
          text: "Book my 15-minute setup",
          theme: "action",
        }}
        secondaryButton={{
          IconTrailing: ArrowUpRightIcon,
          onClick: openLearnMore,
          size: "sm",
          text: "Learn more",
        }}
      />
    </RedoBaseModal>
  );
});
